// The i18next instance (motiflink §1.3).
//
// Contract, in order of how easy it is to break:
//   1. `fallbackLng: false` + `returnEmptyString: true` → a missing Arabic key
//      renders EMPTY, never the English text. Showing English inside an Arabic
//      UI is the specific bug this replaces.
//   2. TS bundles are the base layer; DB rows overlay them via
//      `addResourceBundle(..., deep=true, overwrite=true)` — same language only.
//   3. Interpolation is `{name}`, NOT i18next's default `{{name}}`. The 530
//      ported strings use single braces; changing the strings instead of the
//      config would mean rewriting every one of them.

import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { DEFAULT_LOCALE, LOCALES, type Locale } from './locales';
import ar from './locales/ar';
import en from './locales/en';
import { resolveLanguage } from './resolve';

export const NS = 'translation';

/** Module-load resolution + <html> stamp. Runs before React mounts. */
export const initialLocale: Locale = resolveLanguage();

let instance: I18nInstance | null = null;

export function getI18n(): I18nInstance {
  if (instance) return instance;

  void i18next.use(initReactI18next).init({
    lng: initialLocale,
    supportedLngs: [...LOCALES],
    // Never borrow another language's string. See contract note 1.
    fallbackLng: false,
    returnEmptyString: true,
    // Dot keys are the key itself ('app.nav.home'), not a namespace path.
    defaultNS: NS,
    ns: [NS],
    keySeparator: '.',
    nsSeparator: false,
    resources: {
      ar: { [NS]: ar },
      en: { [NS]: en },
    },
    interpolation: {
      escapeValue: false, // React already escapes
      prefix: '{',
      suffix: '}',
    },
    // A key present in code but absent from a language is a content bug; make
    // it loud in dev, silent (and empty) in prod.
    saveMissing: false,
    missingKeyHandler:
      typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production'
        ? (lngs, _ns, key) => {
            console.warn(`[i18n] missing key "${key}" for ${lngs.join(',')}`);
          }
        : undefined,
    react: { useSuspense: false },
  });

  instance = i18next;
  return i18next;
}

/**
 * Overlay DB rows onto the code bundle for one language.
 * `flat` is the `{ "nav.home": "…" }` map the API returns.
 */
export function applyDbBundle(lang: string, flat: Record<string, string>): void {
  const i18n = getI18n();
  const nested = unflattenDotKeys(flat);
  i18n.addResourceBundle(lang, NS, nested, /* deep */ true, /* overwrite */ true);
}

/** `{ "a.b": "x" }` → `{ a: { b: "x" } }`, mirroring the bundle shape. */
export function unflattenDotKeys(flat: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.');
    let node = out;
    for (let i = 0; i < parts.length - 1; i += 1) {
      const seg = parts[i]!;
      const next = node[seg];
      if (typeof next !== 'object' || next === null) {
        node[seg] = typeof next === 'string' ? { _: next } : {};
      }
      node = node[seg] as Record<string, unknown>;
    }
    node[parts[parts.length - 1]!] = value;
  }
  return out;
}

export { DEFAULT_LOCALE };
